import { SignIn, SignUp } from '@clerk/clerk-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Briefcase } from 'lucide-react';

type Mode = 'signin' | 'signup';

/**
 * Clerk appearance tweaks shared by SignIn/SignUp:
 * - hide the in-card "Don't have an account? Sign up" footer, since we render
 *   our own toggle below the card (avoids duplicate links).
 * - make the Clerk card span the column width so it lines up with the header
 *   and the toggle (its default max-width would leave it offset).
 */
const clerkAppearance = {
  elements: {
    rootBox: 'w-full',
    card: 'w-full max-w-none',
    footerAction: { display: 'none' },
  },
};

export function AuthScreen() {
  const [mode, setMode] = useState<Mode>('signin');

  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-md flex-col justify-center px-5 py-10">
      <header className="mb-6 text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-sm">
          <Briefcase className="h-7 w-7" strokeWidth={2.2} />
        </div>
        <h1 className="text-2xl font-bold tracking-tight">JobFit</h1>
        <p className="mt-2 text-sm font-medium text-foreground/80">
          {mode === 'signin' ? 'Assess every role before you apply.' : 'Assess every role before you apply.'}
        </p>
        <p className="mx-auto mt-2 max-w-xs text-xs leading-relaxed text-muted-foreground">
          Each saved job becomes a clear, three-step readiness check — how much
          you’d want the role, whether your résumé clears ATS screening, and
          whether you’d pass the interview.
        </p>
      </header>

      <div className="w-full">
        {mode === 'signin' ? (
          <SignIn routing="hash" signUpUrl="#/sign-up" appearance={clerkAppearance} />
        ) : (
          <SignUp routing="hash" signInUrl="#/sign-in" appearance={clerkAppearance} />
        )}
      </div>

      <div className="mt-4 text-center text-sm text-muted-foreground">
        {mode === 'signin' ? (
          <>
            No account?{' '}
            <Button
              variant="link"
              size="sm"
              className="h-auto p-0 text-sm"
              onClick={() => setMode('signup')}
            >
              Sign up
            </Button>
          </>
        ) : (
          <>
            Already have an account?{' '}
            <Button
              variant="link"
              size="sm"
              className="h-auto p-0 text-sm"
              onClick={() => setMode('signin')}
            >
              Sign in
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
